/**
 * Activity Detail Page
 * 
 * Displays comprehensive information about a bedroom activity/game
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    ArrowLeft, Heart, Zap, AlertTriangle, Sparkles, Users, Clock,
    ChevronDown, ChevronRight, BookOpen, Star, Activity, Target
} from 'lucide-react';
import api from '../services/api';
import Spinner from '../components/Spinner';

// Section configuration - maps section keys to display info
const SECTION_CONFIG = {
    overview: { icon: Sparkles, title: 'Overview', color: 'text-amber-400' },
    rules: { icon: Star, title: 'How to Play', color: 'text-pink-400' },
    setup: { icon: Target, title: 'Setup & Preparation', color: 'text-blue-400' },
    variations: { icon: Zap, title: 'Variations & Twists', color: 'text-purple-400' },
    difficulty_scaling: { icon: Activity, title: 'Difficulty Scaling', color: 'text-orange-400' },
    props_needed: { icon: Sparkles, title: 'Props & Materials', color: 'text-amber-400' },
    time_management: { icon: Clock, title: 'Timing & Pacing', color: 'text-emerald-400' },
    partner_dynamics: { icon: Users, title: 'Partner Dynamics', color: 'text-rose-400' },
    afterplay: { icon: Heart, title: 'Afterplay & Wrap-up', color: 'text-pink-400' }
};

// Page section component with accordion
const PageSection = ({ sectionKey, content, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const config = SECTION_CONFIG[sectionKey] || { icon: BookOpen, title: sectionKey, color: 'text-gray-400' };
    const Icon = config.icon;

    if (!content) return null;

    return (
        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300 mb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors focus:outline-none"
            >
                <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <h3 className="font-fantasy font-semibold text-lg text-cream/90 tracking-wide">{config.title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-3 border-t border-wine-700/30">
                    <div className="prose prose-invert max-w-none text-cream/70 leading-relaxed">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ActivityDetailPage = () => {
    const { id } = useParams();
    const [activity, setActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchActivity();
    }, [id]);

    const fetchActivity = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getActivity(id);
            setActivity(data);
        } catch (err) {
            console.error('Error fetching activity:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading activity...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !activity) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">🎲</div>
                    <h2 className="text-xl font-bold text-white mb-2">Activity Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This activity does not exist.'}</p>
                    <Link to="/db/activities" className="btn-primary">
                        Back to Activities
                    </Link>
                </div>
            </div>
        );
    }

    // Get page sections by key
    const getSectionContent = (key) => {
        const section = activity.pageSections?.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/db/activities"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Activities</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {activity.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/db/activity/${activity.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {activity.parentVariant.name}
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <header className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-wine-700/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-wine-700/20 flex items-center justify-center">
                                <Star size={28} className="text-wine-400" />
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-3 tracking-wide">
                                    {activity.name}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    {activity.category && (
                                        <span className="px-3 py-1 rounded-full text-sm font-medium border text-pink-400 bg-pink-500/10 border-pink-500/20">
                                            {activity.category}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        {activity.description && (
                            <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed mt-4">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{activity.description}</ReactMarkdown>
                            </div>
                        )}

                        {/* Ornate Divider */}
                        <div className="ornate-divider w-32 my-6" />

                        {/* Key Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Users size={18} className="mx-auto mb-1 text-blue-400" />
                                <div className="text-lg font-bold text-cream">
                                    {activity.min_players === activity.max_players
                                        ? activity.min_players || '?'
                                        : `${activity.min_players || '?'}-${activity.max_players || '?'}`}
                                </div>
                                <div className="text-xs text-dark-400">Players</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Clock size={18} className="mx-auto mb-1 text-emerald-400" />
                                <div className="text-lg font-bold text-cream">{activity.duration_minutes || '?'}</div>
                                <div className="text-xs text-dark-400">Minutes</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Zap size={18} className="mx-auto mb-1 text-amber-400" />
                                <div className="text-lg font-bold text-cream">{activity.intensity_level || '?'}</div>
                                <div className="text-xs text-dark-400">Intensity</div>
                            </div>
                            <div className="text-center p-3 rounded-lg bg-dark-900/50">
                                <Activity size={18} className="mx-auto mb-1 text-purple-400" />
                                <div className="text-lg font-bold text-cream">{activity.category || '—'}</div>
                                <div className="text-xs text-dark-400">Category</div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Rules (if exists as field, not section) */}
                {activity.rules && !getSectionContent('rules') && (
                    <section className="mb-8 p-6 bg-[#0A0A0A]/80 border border-wine-800/30 rounded-xl">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 text-pink-400" />
                            Rules
                        </h3>
                        <p className="text-cream/70 leading-relaxed">{activity.rules}</p>
                    </section>
                )}

                {/* Requirements */}
                {activity.requirements && (
                    <section className="mb-8 p-6 bg-[#0A0A0A]/80 border border-wine-800/30 rounded-xl">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Target className="w-5 h-5 text-amber-400" />
                            Requirements
                        </h3>
                        <p className="text-cream/70 leading-relaxed">{activity.requirements}</p>
                    </section>
                )}

                {/* Page Sections */}
                <div className="space-y-4">
                    {Object.keys(SECTION_CONFIG).map(key => (
                        <PageSection
                            key={key}
                            sectionKey={key}
                            content={getSectionContent(key)}
                            defaultOpen={key === 'overview'}
                        />
                    ))}
                </div>

                {/* Sub-Variants */}
                {activity.childVariants && activity.childVariants.length > 0 && (
                    <section className="mt-8">
                        <h3 className="text-lg font-fantasy font-semibold text-cream/90 mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 text-pink-400" />
                            Variants of {activity.name}
                        </h3>
                        <div className="space-y-2">
                            {activity.childVariants.map(variant => (
                                <Link
                                    key={variant.id}
                                    to={`/db/activity/${variant.id}`}
                                    className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
                                >
                                    <div>
                                        <span className="text-white group-hover:text-wine-300 transition-colors">{variant.name}</span>
                                        {variant.category && <span className="ml-2 text-sm text-dark-500">{variant.category}</span>}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ActivityDetailPage;
